import { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { SearchIcon, XCircleIcon } from '@/components/fun-ui/icons';
import Loading from '@/components/Loading';
import { cn } from '@/lib/utils';

interface SearchProps {
  onSearch: (searchString: string) => Promise<void>;
  debounceTime?: number;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  className?: string;
  containerClassName?: string;
  iconSize?: number;
  iconClassName?: string;
  showClearButton?: boolean;
  showLoading?: boolean;
}

export const Searcher = ({
  onSearch,
  debounceTime = 300,
  placeholder = '搜索文献标题、作者、机构',
  defaultValue = '',
  value,
  className,
  containerClassName,
  iconSize = 20,
  iconClassName = '',
  showClearButton = true,
  showLoading = true,
}: SearchProps) => {
  // 内部维护输入框的值，无论是受控还是非受控模式
  const [internalValue, setInternalValue] = useState(defaultValue || value || '');

  // 当 props.value 变化时（受控模式），同步到内部状态
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof debounce> | null>(null);

  // 初始化防抖函数
  useEffect(() => {
    const debouncedSearch = debounce(async (query: string) => {
      setIsSearching(true);
      try {
        await onSearch(query);
      } catch (error) {
        console.error('搜索错误:', error);
      } finally {
        setIsSearching(false);
      }
    }, debounceTime);

    debounceRef.current = debouncedSearch;

    return () => {
      debouncedSearch.cancel();
    };
  }, [onSearch, debounceTime]);

  const isComposingRef = useRef<boolean>(false);


  // 处理输入变化
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // 始终更新内部状态，保证输入框能正常显示用户的输入（包括拼音过程）
    setInternalValue(newValue);

    // 只有在非拼音输入状态下才触发搜索
    if (!isComposingRef.current) {
      debounceRef.current?.(newValue);
    }
  };

  // 清空输入并触发全量搜索
  const handleClear = useCallback(async () => {
    // 始终更新内部状态
    setInternalValue('');

    // 立即触发空字符串搜索（不经过防抖）
    setIsSearching(true);
    try {
      await onSearch('');
    } catch (error) {
      console.error('搜索错误:', error);
    } finally {
      setIsSearching(false);
    }
  }, [onSearch]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      // 取消正在进行的防抖搜索
      debounceRef.current?.cancel();
    };
  }, [])

  return (
    <div className={cn("relative flex items-center", containerClassName)}>
      <SearchIcon
        size={iconSize}
        className={cn(
          "absolute left-2.5 text-annotation-foreground pointer-events-none",
          iconClassName,
        )} />
      <Input
        maxLength={150}
        type="text"
        value={internalValue}
        onChange={handleChange}
        placeholder={placeholder}
        className={cn(
          "pl-10 pr-8 rounded-lg border focus:outline-none m-0 w-[260px] placeholder:font-normal font-normal",
          className
        )}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={(e) => {
          isComposingRef.current = false;
          // 拼音输入结束后触发搜索
          debounceRef.current?.(e.currentTarget.value);
        }}
      />
      {showClearButton && internalValue && !isSearching && (
        <button
          onClick={handleClear}
          className="absolute right-3 text-muted-foreground/50 hover:text-muted-foreground focus:outline-none"
          aria-label="清除搜索"
        >
          <XCircleIcon size={16} />
        </button>
      )}
      {showLoading && isSearching && (
        <div className="absolute right-3">
          <Loading size="sm" />
        </div>
      )}
    </div>
  );
};

// 防抖函数实现
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): { (...args: Parameters<T>): void; cancel(): void } {
  let timeout: NodeJS.Timeout | null = null;

  const debounced = (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}